import { LayoutDashboard, FileText, Car, Settings, FileCheck, LogOut, CreditCard, BarChart3, PanelLeft, Stethoscope, Sparkles } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useTipoOperacao } from "@/hooks/useTipoOperacao";
import { Badge } from "@/components/ui/badge";

const adminItems = [
  { title: "Relatórios", url: "/relatorios", icon: BarChart3 },
]

const AppSidebar = () => {
  const { state, isMobile, setOpenMobile } = useSidebar()
  const location = useLocation()
  const navigate = useNavigate()
  const { signOut, perfil, loja } = useAuth()
  const { plano, isTrial, trialAvaliacoesRestantes } = useSubscription()
  const vertical = useTipoOperacao()
  const isAdmin = perfil?.papel === 'admin'
  const currentPath = location.pathname
  const isCollapsed = state === "collapsed"
  const isActive = (path: string) => currentPath === path

  const commonItems = [
    { title: "Painel", url: "/dashboard", icon: LayoutDashboard },
    { title: "Veículos", url: "/veiculos", icon: Car },
    { title: vertical.labels.menuPrincipal, url: "/avaliacoes", icon: FileText },
    ...(vertical.tipo === "oficina" ? [{ title: "Diagnósticos", url: "/diagnosticos", icon: Stethoscope }] : []),
    ...(vertical.tipo === "loja" ? [{ title: vertical.labels.menuDocumentos, url: "/propostas", icon: FileCheck }] : []),
  ]

  function handleNavigateFromMobileMenu() {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  async function handleLogout() {
    await signOut()
    navigate('/login')
  }

  return (
    <Sidebar className={isCollapsed ? "w-14" : "w-64"} collapsible="icon">
      <SidebarHeader className="border-b px-4 py-4">
        <div className={`flex items-center ${isCollapsed ? "justify-center" : "justify-between"} gap-3`}>
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Car className="h-4 w-4 text-primary-foreground" />
            </div>
            {!isCollapsed && (
              <div>
                <h2 className="font-semibold text-lg">WeAuto</h2>
                <p className="text-xs text-muted-foreground">Sistema 360°</p>
              </div>
            )}
          </div>
        </div>
        <div className={`${isCollapsed ? "mt-3 flex justify-center" : "mt-4"}`}>
          <SidebarTrigger
            className={
              isCollapsed
                ? "h-8 w-8 rounded-md"
                : "h-9 w-full justify-start rounded-md border border-border/60 bg-background/70 px-3 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            }
          >
            {isCollapsed ? null : (
              <>
                <PanelLeft className="h-4 w-4" />
                <span>Recolher menu</span>
              </>
            )}
          </SidebarTrigger>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Menu Principal</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {commonItems.map(item => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <NavLink to={item.url} end onClick={handleNavigateFromMobileMenu}>
                      <item.icon className="h-4 w-4" />
                      {!isCollapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              {isAdmin && adminItems.map(item => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <NavLink to={item.url} onClick={handleNavigateFromMobileMenu}>
                      <item.icon className="h-4 w-4" />
                      {!isCollapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {isAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive("/planos")}>
                    <NavLink to="/planos" onClick={handleNavigateFromMobileMenu}>
                      <CreditCard className="h-4 w-4" />
                      {!isCollapsed && (
                        <div className="flex items-center gap-2 flex-1">
                          <span>Planos</span>
                          {isTrial ? (
                            <Badge variant="secondary" className="text-xs">Trial {trialAvaliacoesRestantes}/5</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs capitalize">{plano}</Badge>
                          )}
                        </div>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/configuracoes")}>
                  <NavLink to="/configuracoes" onClick={handleNavigateFromMobileMenu}>
                    <Settings className="h-4 w-4" />
                    {!isCollapsed && <span>Configurações</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <div className="mt-auto p-4 border-t space-y-2">
        {!isCollapsed && loja?.nome && (
          <div className="rounded-md bg-muted/40 px-2 py-2">
            <p className="line-clamp-2 text-xs font-medium text-foreground">{loja.nome}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!isCollapsed && <span>Sair</span>}
        </button>
      </div>
    </Sidebar>
  )
}

export { AppSidebar }
export default AppSidebar
